import React, { useState } from "react";

import { mdiChevronDown, mdiEarth } from "@mdi/js";
import Icon from "@mdi/react";
import classNames from "classnames";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { setLocale } from "../../actions/VariableActions";
import useOutsideClick from "../../hooks/useOutsideClick";

import type { JSX } from "react";

const languages = ["en", "fr"];

interface MenuLangSelectorProps {
  isCollapsed?: boolean;
  collapsedSidebarClassNames: (isCollapsed?: boolean) => string;
}

const PORTAL_ID = "language-selector-portal";

function MenuLangSelector({
  isCollapsed,
  collapsedSidebarClassNames,
}: MenuLangSelectorProps): JSX.Element {
  const { t, i18n } = useTranslation("languages");
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useOutsideClick((target) => {
    if (isOpen && target?.id !== PORTAL_ID) {
      setIsOpen(false);
    }
  });

  const changeLanguage = (lng: string): void => {
    i18n.changeLanguage(lng);
    dispatch(setLocale(lng));
    setIsOpen(false);
  };

  const containerClassName = classNames(
    "flex w-full cursor-pointer items-center rounded-[0.46875rem] py-[0.3125rem] px-[0.625rem] hover:bg-surface-muted gap-[0.78125rem]",
    isOpen && "bg-surface-muted",
  );

  const chevronIconClassNames = classNames(
    "transition-transform ease-in-out",
    isOpen ? "rotate-180" : "rotate-0",
  );

  const iconContainerClassNames = classNames(
    "flex w-[55px] items-center justify-center transition-transform duration-300 ease-in-out",
    isCollapsed ? "-translate-x-2" : "translate-x-0",
  );

  const languageTextClassNames = classNames(
    "flex items-center justify-center",
    collapsedSidebarClassNames(isCollapsed),
    isCollapsed && "w-0",
  );

  const portalPosition = ref.current?.getBoundingClientRect();
  return (
    <div ref={ref} className="relative mx-[0.78125rem] my-[0.3125rem]">
      <div
        className={containerClassName}
        onClick={(): void => setIsOpen(!isOpen)}
      >
        <div className={iconContainerClassNames}>
          <Icon path={mdiEarth} size={1} />
        </div>
        <div className={languageTextClassNames}>
          <div className="uppercase">{t(`langs.${i18n.language}`)}</div>
          <div className="flex w-[55px] items-center justify-center">
            <Icon
              className={chevronIconClassNames}
              path={mdiChevronDown}
              size={1}
            />
          </div>
        </div>
      </div>
      {isOpen
        ? createPortal(
            <div
              className="absolute z-[1000] mb-[0.15625rem] min-w-[230px]"
              style={{
                left: portalPosition ? portalPosition.left : 0,
                top: portalPosition
                  ? portalPosition.top -
                    8 -
                    portalPosition.height * languages.length
                  : 0,
              }}
            >
              <div className="border-border bg-muted flex flex-col rounded-[0.3125rem] border">
                {languages.map((lng) => (
                  <div
                    key={lng}
                    className="text-foreground hover:bg-surface-muted cursor-pointer p-[0.46875rem] text-center uppercase"
                    id={PORTAL_ID}
                    onClick={(): void => changeLanguage(lng)}
                  >
                    {t(`langs.${lng}`)}
                  </div>
                ))}
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

export default MenuLangSelector;
